// HAPPYCAP_CHAT_DESKTOP_LAYOUT_V1
import api from "../../services/api";
import { optimizeImage } from "../../utils/optimizeImage";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import Sidebar from "./Sidebar";
import Conversation from "./Conversation";
import ModalAvisoChat from "./ModalAvisoChat";
import {
  getChatHistory,
  marcarMensajesLeidos,
} from "../../services/api";
import { useAuth } from "../../auth/AuthContext";
import { useWebSocketActions } from "../../context/WebSocketContext";
import { useNotification } from "../../context/NotificationContext";
import toast from "react-hot-toast";

const ChatPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { setActiveChatUserId, fetchUnreadMessages, unreadBySender } = useNotification();
  const { sendJsonMessage, addListener, isConnected } = useWebSocketActions();

  const [selectedFriend, setSelectedFriend] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [showModal, setShowModal] = useState(() => {
      return localStorage.getItem("chat_modal_acknowledged") !== "true";
    });

  const userProfileId = user?.id;
  // HAPPYCAP_PHOTO_ACTIVE_CHAT_REF_V1
  const activePhotoFriendRef = useRef(selectedFriend?.id ?? null);
  activePhotoFriendRef.current = selectedFriend?.id ?? null;

  // HAPPYCAP_PHOTO_DELETE_RACE_FIX_V1
  const deletedPhotoIdsRef = useRef(new Set());

  const removeDeletedPhotos = (items) =>
    items.filter(
      (msg) =>
        !deletedPhotoIdsRef.current.has(String(msg.id))
    );

  const messagesRef = useRef(messages);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // HAPPYCAP_CHAT_CHRONOLOGICAL_ORDER_V1
  // Mostrar siempre los mensajes antiguos arriba y los nuevos abajo.
  const normalizeMessages = (msgs, currentUserProfileId) =>
    [...msgs].sort((a, b) => {
      const fechaA = Date.parse(a.timestamp || a.created_at || "") || 0;
      const fechaB = Date.parse(b.timestamp || b.created_at || "") || 0;
      return fechaA - fechaB;
    }).map((msg) => {
      const senderProfileId = Number(msg.sender_id ?? msg.sender?.id);
      return {
        ...msg,
        sender_id: senderProfileId,
        sender_nombre: msg.sender_nombre ?? msg.sender?.nombre ?? "",
        sender_apellidos: msg.sender_apellidos ?? msg.sender?.apellidos ?? "",
        isOwn: senderProfileId === Number(currentUserProfileId),
      };
    });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const saveMessagesToLocal = (friendId, msgs) => {
    // HAPPYCAP_PHOTOS_NO_LOCALSTORAGE_V2
    msgs = msgs.filter(
      (msg) =>
        msg.type !== "photo" &&
        !String(msg.id ?? "").startsWith("photo_")
    );

    const pureMsgs = msgs.map(({ isOwn, ...rest }) => rest);
    localStorage.setItem(`chat_messages_${friendId}`, JSON.stringify(pureMsgs));
  };

  const loadMessagesFromLocal = (friendId) => {
    const saved = localStorage.getItem(`chat_messages_${friendId}`);
    const rawMsgs = saved ? JSON.parse(saved) : [];
    // HAPPYCAP_PHOTO_LOCAL_READ_FILTER_V2
    const textOnly = Array.isArray(rawMsgs)
      ? rawMsgs.filter(
          (msg) =>
            msg.type !== "photo" &&
            !String(msg.id ?? "").startsWith("photo_")
        )
      : [];

    return normalizeMessages(textOnly, userProfileId);
  };

    const handleModalClose = () => {
      localStorage.setItem("chat_modal_acknowledged", "true");
      setShowModal(false);
    };

  useEffect(() => {
    if (!selectedFriend) return;

    setActiveChatUserId(selectedFriend.id);
    fetchUnreadMessages();

    const removeListener = addListener((data) => {
      if (!data) return;

      if (data.type === "error" && data.message?.includes("bloqueado")) {
        toast.error(`${t("chat.error_not_allowed")}\n${t("chat.error_not_sent")}`);
        return;
      }

      // HAPPYCAP_PHOTO_DELETE_REACT_V1
      if (data.type === "photo_deleted") {
        const deletedId = String(data.photo_id ?? "");

        if (!/^photo_[1-9][0-9]*$/.test(deletedId)) return;

        deletedPhotoIdsRef.current.add(deletedId);

        setMessages((previous) =>
          previous.filter(
            (msg) => String(msg.id) !== deletedId
          )
        );

        return;
      }

      if (data.type === "read_receipt") {
        const { reader_id } = data;
        setMessages((prev) =>
          prev.map((msg) =>
            msg.receiver_id === reader_id && msg.sender_id === userProfileId
              ? { ...msg, read: true }
              : msg
          )
        );
        return;
      }

      const isRelevant =
        data.sender_id === selectedFriend.id ||
        data.receiver_id === selectedFriend.id;

      const alreadyExists = messagesRef.current.some(
        (msg) =>
          (msg.id && data.id && msg.id === data.id) ||
          (msg.temp_id && data.temp_id && msg.temp_id === data.temp_id)
      );

      if (!isRelevant || alreadyExists) return;

      const normalizedMessage = normalizeMessages([data], userProfileId)[0];
      const updated = [...messagesRef.current, normalizedMessage];

      setMessages(updated);
      saveMessagesToLocal(selectedFriend.id, updated);

      if (!normalizedMessage.isOwn && normalizedMessage.sender_id === selectedFriend.id) {
        marcarMensajesLeidos(selectedFriend.id);
        fetchUnreadMessages();
      }
    });

    return () => {
      removeListener();
      setActiveChatUserId(null);
    };
  }, [selectedFriend, addListener, setActiveChatUserId, fetchUnreadMessages, userProfileId]);

  useEffect(() => {
    const loadHistoryAndMarkRead = async () => {
      if (!selectedFriend) return;

      const localMsgs = loadMessagesFromLocal(selectedFriend.id);
      setMessages(localMsgs);

      try {
        const res = await getChatHistory(selectedFriend.id);
        const serverMsgs = normalizeMessages(res.data || [], userProfileId);
        await marcarMensajesLeidos(selectedFriend.id);
        await fetchUnreadMessages();
        const visibleMsgs = removeDeletedPhotos(serverMsgs);

        if (
          Number(activePhotoFriendRef.current) !==
          Number(selectedFriend.id)
        ) {
          return;
        }

        setMessages(visibleMsgs);
        saveMessagesToLocal(selectedFriend.id, visibleMsgs);

        sendJsonMessage({
          action: "mark_read",
          sender_id: selectedFriend.id,
        });
      } catch (err) {
        console.warn("⚠️ Error cargando historial del servidor", err);
      }
    };

    loadHistoryAndMarkRead();
  }, [selectedFriend, fetchUnreadMessages, userProfileId]);

  // HAPPYCAP_CHAT_PHOTO_UPLOAD_V1
  const handleSendPhoto = async (file) => {
    if (!selectedFriend) {
      throw new Error("No hay una conversación seleccionada.");
    }

    if (!file || !file.type.startsWith("image/")) {
      throw new Error("Selecciona una fotografía válida.");
    }

    const receiverId = selectedFriend.id;
    const optimized = await optimizeImage(file);

    if (optimized.size > 8 * 1024 * 1024) {
      throw new Error("La fotografía supera los 8 MB.");
    }

    const formData = new FormData();
    formData.append("receiver_id", String(receiverId));
    formData.append("image", optimized);

    // HAPPYCAP_PHOTO_HTTP_CONFIRMATION_V1
    const response = await api.post("/chat/fotos/", formData);
    const photo = response.data;

    if (!photo || photo.type !== "photo" || !photo.id) {
      throw new Error("El servidor no devolvió la fotografía esperada.");
    }

    // No insertar la foto en otra conversación si el usuario
    // cambió de chat mientras se completaba la subida.
    const activeFriendId = activePhotoFriendRef.current;

    if (
      activeFriendId == null ||
      Number(activeFriendId) !== Number(receiverId)
    ) {
      return;
    }

    // El WebSocket puede haber entregado la misma fotografía.
    // Comprobar su ID evita mostrarla dos veces.
    const alreadyExists = messagesRef.current.some(
      (msg) => String(msg.id) === String(photo.id)
    );

    if (!alreadyExists) {
      const normalized = normalizeMessages([photo], userProfileId)[0];

      setMessages((previous) => {
        if (
          previous.some(
            (msg) => String(msg.id) === String(photo.id)
          )
        ) {
          return previous;
        }

        return [...previous, normalized];
      });
    }

    // Las fotografías siguen excluidas de localStorage.
  };

  const handlePhotoDeleted = (photoId) => {
    const deletedId = String(photoId ?? "");

    if (!/^photo_[1-9][0-9]*$/.test(deletedId)) return;

    deletedPhotoIdsRef.current.add(deletedId);

    setMessages((previous) =>
      previous.filter(
        (msg) => String(msg.id) !== deletedId
      )
    );
  };

  const handleSend = (msgText) => {
    if (!selectedFriend || !msgText.trim()) return;

    const tempId = uuidv4();

    const tempMessage = {
      id: tempId,
      temp_id: tempId,
      sender_id: userProfileId,
      sender_nombre: user?.nombre,
      sender_apellidos: user?.apellidos,
      receiver_id: selectedFriend.id,
      message: msgText,
      timestamp: new Date().toISOString(),
      read: false,
    };

    const normalizedTemp = normalizeMessages([tempMessage], userProfileId)[0];
    const updated = [...messagesRef.current, normalizedTemp];

    setMessages(updated);
    saveMessagesToLocal(selectedFriend.id, updated);

    if (isConnected) {
      sendJsonMessage({
        receiver_id: selectedFriend.id,
        message: msgText,
        temp_id: tempId,
        active_conversation_with: selectedFriend.id,
      });
    } else {
      console.warn("📡 WebSocket no conectado. Mensaje solo local.");
    }
  };

  // Ocultar navegación global mientras se conversa en móvil.
  useEffect(() => {
    const pantallaCompleta = isMobile && Boolean(selectedFriend);

    document.body.classList.toggle(
      "happycap-chat-fullscreen",
      pantallaCompleta
    );

    return () => {
      document.body.classList.remove("happycap-chat-fullscreen");
    };
  }, [isMobile, selectedFriend]);

  return (
    <div className={`fixed inset-x-0 z-0 flex min-h-0 overflow-hidden bg-[#f4f7fa] md:bottom-0 ${
      isMobile && selectedFriend
        ? "top-0 bottom-0"
        : "top-[52px] bottom-[64px] md:top-[76px] md:bottom-0"
    }`}>
        {showModal && <ModalAvisoChat onClose={handleModalClose} />}


      {isMobile ? (
        selectedFriend ? (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <Conversation
              friend={selectedFriend}
              messages={messages}
              onSend={handleSend}
              onSendPhoto={handleSendPhoto}
              onPhotoDeleted={handlePhotoDeleted}
              currentUserId={userProfileId}
              onBackMobile={() => setSelectedFriend(null)}
            />
          </div>
        ) : (
          <Sidebar
            onSelectFriend={setSelectedFriend}
            selectedFriend={selectedFriend}
          />
        )
      ) : (
        <>
          <Sidebar
            onSelectFriend={setSelectedFriend}
            selectedFriend={selectedFriend}
            unreadBySender={unreadBySender}
          />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            {!selectedFriend ? (
              <div className="flex-1" aria-hidden="true" />
            ) : (
              <Conversation
                friend={selectedFriend}
                messages={messages}
                onSend={handleSend}
              onSendPhoto={handleSendPhoto}
              onPhotoDeleted={handlePhotoDeleted}
                currentUserId={userProfileId}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ChatPage;
